import ButtonJustifyEnd from "components/ButtonJustifyEnd";
import Loading from "components/Loading";
import Title from "components/Title";
import toastMessage from "helpers/handleToastMessage";
import IconCheck from "icons/confirm-circle-white.svg";
import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { ToastContainer } from "react-toastify";
import { memberAddStep3, sync, userCreateStep3 } from "services/manageFamilies";
import { checkUser } from "services/user";
import { schemaFamilyDetails, formatDataForValidation } from "./validation";
import PersonInput from "components/PersonInput";

type Props = {
  groupChildrenName: string;
};

type Person = {
  email: string;
  firstName: string;
  lastName: string;
};

const FamilyDetails: React.FC<Props> = ({ groupChildrenName }): JSX.Element => {
  const history = useHistory();
  const [groupId, setGroupId] = useState("");
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<any>({});

  const [child, setChild] = useState<Person>({
    email: "",
    firstName: "",
    lastName: "",
  });

  const [adoptiveParents, setAdoptiveParents] = useState<Person[]>([
    { email: "", firstName: "", lastName: "" },
    { email: "", firstName: "", lastName: "" },
  ]);

  const [biologicalParents, setBiologicalParents] = useState<Person[]>([
    { email: "", firstName: "", lastName: "" },
    { email: "", firstName: "", lastName: "" },
  ]);

  const [userIds, setUserIds] = useState<Record<string, string>>({});

  useEffect(() => {
    const groupId = localStorage.getItem("groupId");
    if (groupId) {
      setGroupId(groupId);
    }

    if (groupChildrenName) {
      const [firstName, lastName] = groupChildrenName.split(" ");
      setChild((prev) => ({ ...prev, firstName, lastName }));
    }
  }, [groupChildrenName]);

  const checkEmail = useCallback(
    async (email: string, role: string) => {
      if (!email) return;

      try {
        setLoading(true);
        const { data } = await checkUser(email);
        const [firstName, lastName] = data.name.split(" ");

        if (role === "child") {
          setChild({ email, firstName, lastName });
          setUserIds((prev) => ({ ...prev, child: data.id }));
        } else if (role.startsWith("adoptive")) {
          const index = Number(role.split("[")[1].split("]")[0]);
          const newAdoptiveParents = [...adoptiveParents];
          newAdoptiveParents[index] = { email, firstName, lastName };
          setAdoptiveParents(newAdoptiveParents);
          setUserIds((prev) => ({
            ...prev,
            [`adoptiveParents${index}`]: data.id,
          }));
        } else if (role.startsWith("biological")) {
          const index = Number(role.split("[")[1].split("]")[0]);
          const newBiologicalParents = [...biologicalParents];
          newBiologicalParents[index] = { email, firstName, lastName };
          setBiologicalParents(newBiologicalParents);
          setUserIds((prev) => ({
            ...prev,
            [`biologicalParents${index}`]: data.id,
          }));
        }
      } catch (_) {
        if (role === "child") {
          setUserIds((prev) => ({ ...prev, child: "" }));
        } else if (role.startsWith("adoptive")) {
          const index = Number(role.slice(-1));
          setUserIds((prev) => ({ ...prev, [`adoptive${index}`]: "" }));
        } else if (role.startsWith("biological")) {
          const index = Number(role.slice(-1));
          setUserIds((prev) => ({ ...prev, [`biological${index}`]: "" }));
        }
      } finally {
        setLoading(false);
      }
    },
    [adoptiveParents, biologicalParents]
  );

  const handleSubmit = async () => {
    try {
      setLoading(true);
      const formattedData = formatDataForValidation({
        childEmail: child.email,
        childFirstName: child.firstName,
        childSecondName: child.lastName,
        firstEmailAdoptiveFamily: adoptiveParents[0].email,
        firstFirstNameAdoptiveFamily: adoptiveParents[0].firstName,
        firstLastNameAdoptiveFamily: adoptiveParents[0].lastName,
        secondEmailAdoptiveFamily: adoptiveParents[1].email,
        secondFirstNameAdoptiveFamily: adoptiveParents[1].firstName,
        secondLastNameAdoptiveFamily: adoptiveParents[1].lastName,
        firstEmailBiologicFamily: biologicalParents[0].email,
        firstFirstNameBiologicFamily: biologicalParents[0].firstName,
        firstLastNameBiologicFamily: biologicalParents[0].lastName,
        secondEmailBiologicFamily: biologicalParents[1].email,
        secondFirstNameBiologicFamily: biologicalParents[1].firstName,
        secondLastNameBiologicFamily: biologicalParents[1].lastName,
      });

      try {
        await schemaFamilyDetails.validate(formattedData, {
          abortEarly: false,
        });
      } catch (validationError: any) {
        const formattedErrors = {};
        validationError.inner.forEach((error: any) => {
          const path = error.path.split(".");
          if (path.length === 2) {
            const [group, fieldWithIndex] = path;
            if (fieldWithIndex.includes("[")) {
              const match = fieldWithIndex.match(/\[(\d+)\]/);
              if (match) {
                const index = match[1];
                (formattedErrors as any)[`${group}[${index}]email`] =
                  error.message;
                (formattedErrors as any)[`${group}[${index}]firstName`] =
                  error.message;
                (formattedErrors as any)[`${group}[${index}]lastName`] =
                  error.message;
              }
            } else {
              (formattedErrors as any)[`${group}${fieldWithIndex}`] =
                error.message;
            }
          } else if (path.length === 3) {
            const [group, index, field] = path;
            (formattedErrors as any)[`${group}[${index}]${field}`] =
              error.message;
          }
        });

        setErrors(formattedErrors);
        setLoading(false);
        return;
      }

      const newUsers = [];
      const addMembers = [];

      if (child.email && child.firstName && child.lastName) {
        if (!userIds.child && child.email) {
          newUsers.push({
            name: `${child.firstName} ${child.lastName}`,
            email: child.email,
            subtype: "child",
            groupId: groupId,
          });
        } else if (userIds.child) {
          addMembers.push({
            groupId,
            role: "child",
            userId: userIds.child,
          });
        }
      }

      if (
        adoptiveParents[0].email &&
        adoptiveParents[0].firstName &&
        adoptiveParents[0].lastName
      ) {
        if (!userIds.adoptiveParents0 && adoptiveParents[0].email) {
          newUsers.push({
            name: `${adoptiveParents[0].firstName} ${adoptiveParents[0].lastName}`,
            email: adoptiveParents[0].email,
            subtype: "adoptive parents",
            groupId: groupId,
          });
        } else if (adoptiveParents[0].email) {
          addMembers.push({
            groupId,
            role: "adoptive parents",
            userId: userIds.adoptiveParents0,
          });
        }
      }

      if (
        adoptiveParents[1].email &&
        adoptiveParents[1].firstName &&
        adoptiveParents[1].lastName
      ) {
        if (!userIds.adoptiveParents1 && adoptiveParents[1].email) {
          newUsers.push({
            name: `${adoptiveParents[1].firstName} ${adoptiveParents[1].lastName}`,
            email: adoptiveParents[1].email,
            subtype: "adoptive parents",
            groupId: groupId,
          });
        } else if (adoptiveParents[1].email) {
          addMembers.push({
            groupId,
            role: "adoptive parents",
            userId: userIds.adoptiveParents1,
          });
        }
      }

      if (
        biologicalParents[0].email &&
        biologicalParents[0].firstName &&
        biologicalParents[0].lastName
      ) {
        if (!userIds.biologicalParents0 && biologicalParents[0].email) {
          newUsers.push({
            name: `${biologicalParents[0].firstName} ${biologicalParents[0].lastName}`,
            email: biologicalParents[0].email,
            subtype: "birth family",
            groupId: groupId,
          });
        } else if (userIds.biologicalParents0) {
          addMembers.push({
            groupId,
            role: "birth family",
            userId: userIds.biologicalParents0,
          });
        }
      }

      if (
        biologicalParents[1].email &&
        biologicalParents[1].firstName &&
        biologicalParents[1].lastName
      ) {
        if (!userIds.biologicalParents1 && biologicalParents[1].email) {
          newUsers.push({
            name: `${biologicalParents[1].firstName} ${biologicalParents[1].lastName}`,
            email: biologicalParents[1].email,
            subtype: "birth family",
            groupId: groupId,
          });
        } else if (biologicalParents[1].email) {
          addMembers.push({
            groupId,
            role: "birth family",
            userId: userIds.biologicalParents1,
          });
        }
      }

      let syncGetStream: any[] = [];

      if (newUsers.length > 0) {
        const promisesNewUsers = newUsers.map((user) => userCreateStep3(user));
        const data = await Promise.all(promisesNewUsers);
        const users = data.map((user) => user.data);
        syncGetStream = syncGetStream.concat(users);
      }

      if (addMembers.length > 0) {
        const promisesAddMembers = addMembers.map((member) =>
          memberAddStep3(member)
        );
        const data = await Promise.all(promisesAddMembers);
        const users = data.map((user) => user.data);
        syncGetStream = syncGetStream.concat(users);
      }

      await sync(syncGetStream);

      toastMessage({
        message: "Group created.",
        type: "success",
      });

      localStorage.removeItem("groupId");
      history.push(`/manage-families/${groupId}/details`);
    } catch (error: any) {
      toastMessage({
        message: error.response.data.error
          ? error.response.data.error
          : "Error, Something went wrong.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const isFirstAdoptiveComplete =
    adoptiveParents[0].email &&
    adoptiveParents[0].firstName &&
    adoptiveParents[0].lastName;

  const isFirstBiologicalComplete =
    biologicalParents[0].email &&
    biologicalParents[0].firstName &&
    biologicalParents[0].lastName;

  return (
    <div className="bg-bt-white p-4">
      <Loading loading={loading} />
      <div className="w-9/12 mx-auto mt-4">
        <Title title={"Add Family Details"} />

        <PersonInput
          title="Child"
          people={[child]}
          setPeople={(people) => setChild(people[0])}
          errors={errors}
          checkEmail={checkEmail}
          rolePrefix="child"
          maxPeople={1}
        />

        <PersonInput
          title="Adoptive Family"
          people={adoptiveParents}
          setPeople={setAdoptiveParents}
          errors={errors}
          checkEmail={checkEmail}
          rolePrefix="adoptiveParents"
          isSecondDisabled={!isFirstAdoptiveComplete}
        />

        <PersonInput
          title="Biological Family"
          people={biologicalParents}
          setPeople={setBiologicalParents}
          errors={errors}
          checkEmail={checkEmail}
          rolePrefix="biologicalParents"
          isSecondDisabled={!isFirstBiologicalComplete}
        />
      </div>

      <ButtonJustifyEnd
        title={"Confirm"}
        iconLeft={IconCheck}
        action={handleSubmit}
      />
      <ToastContainer />
    </div>
  );
};

export default FamilyDetails;
